import { useState, useEffect } from 'react';
import { ArrowLeft, Moon, Clock } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function PrayerTimesView({ senior, onClose }) {
  const [prayerTimes, setPrayerTimes] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchPrayerTimes();
  }, []);

  const fetchPrayerTimes = async () => {
    try {
      setLoading(true);
      if (!senior?.id) {
        throw new Error('Senior ID is required');
      }
      const response = await axios.get(`${API}/api/prayer-times/${senior.id}`);
      setPrayerTimes(response.data);
    } catch (err) {
      console.error('Error fetching prayer times:', err);
      setError('Gebetszeiten konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  };

  const getLanguageText = (key) => {
    const lang = senior?.language || 'de';
    const translations = {
      de: {
        title: 'Gebetszeiten',
        back: 'Zurück',
        loading: 'Laden...',
        error: 'Fehler beim Laden',
        nextPrayer: 'Nächstes Gebet',
        fajr: 'Fajr (Morgengebet)',
        dhuhr: 'Dhuhr (Mittagsgebet)',
        asr: 'Asr (Nachmittagsgebet)',
        maghrib: 'Maghrib (Abendgebet)',
        isha: 'Isha (Nachtgebet)',
      },
      tr: {
        title: 'Namaz Vakitleri',
        back: 'Geri',
        loading: 'Yükleniyor...',
        error: 'Yükleme hatası',
        nextPrayer: 'Sıradaki Namaz',
        fajr: 'Sabah',
        dhuhr: 'Öğle',
        asr: 'İkindi',
        maghrib: 'Akşam',
        isha: 'Yatsı',
      },
      ar: {
        title: 'أوقات الصلاة',
        back: 'رجوع',
        loading: 'جار التحميل...',
        error: 'خطأ في التحميل',
        nextPrayer: 'الصلاة القادمة',
        fajr: 'الفجر',
        dhuhr: 'الظهر',
        asr: 'العصر',
        maghrib: 'المغرب',
        isha: 'العشاء',
      }
    };
    return translations[lang]?.[key] || translations.de[key];
  };

  const getPrayerColor = (prayer) => {
    const colors = {
      // Islamic prayers
      fajr: 'border-blue-300 bg-blue-50',
      dhuhr: 'border-amber-300 bg-amber-50',
      asr: 'border-orange-300 bg-orange-50',
      maghrib: 'border-purple-300 bg-purple-50',
      isha: 'border-indigo-300 bg-indigo-50',
      // Christian prayers
      laudes: 'border-yellow-300 bg-yellow-50',
      terz: 'border-green-300 bg-green-50',
      sext: 'border-blue-300 bg-blue-50',
      non: 'border-orange-300 bg-orange-50',
      vesper: 'border-purple-300 bg-purple-50',
      komplet: 'border-indigo-300 bg-indigo-50',
    };
    return colors[prayer.toLowerCase()] || 'border-gray-300 bg-gray-50';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-indigo-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-24 w-24 border-b-4 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-900 text-3xl font-bold">{getLanguageText('loading')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-indigo-50 flex items-center justify-center p-4 md:p-8">
        <Card className="bg-white shadow-lg border-2 border-red-200">
          <CardContent className="pt-12 pb-12 text-center">
            <Moon className="w-32 h-32 mx-auto mb-6 text-red-400" />
            <p className="text-4xl font-bold text-red-600 mb-4">{getLanguageText('error')}</p>
            <p className="text-2xl text-gray-600">{error}</p>
            <button
              onClick={onClose}
              className="mt-8 px-8 py-4 bg-white border-3 border-gray-300 hover:bg-gray-50 text-gray-900 rounded-2xl text-2xl font-bold shadow-lg"
            >
              {getLanguageText('back')}
            </button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-indigo-50 p-4 md:p-8">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-4xl md:text-5xl font-bold text-gray-900">{getLanguageText('title')}</h1>
        <button
          onClick={onClose}
          className="px-6 py-3 bg-white border-3 border-gray-300 hover:bg-gray-50 text-gray-900 rounded-2xl flex items-center gap-3 shadow-lg"
        >
          <ArrowLeft className="w-10 h-10" />
          <span className="text-2xl font-bold">{getLanguageText('back')}</span>
        </button>
      </div>

      {/* Date Info */}
      {prayerTimes?.date && (
        <Card className="bg-gradient-to-r from-purple-400 to-indigo-400 text-white shadow-2xl mb-6">
          <CardContent className="pt-6 pb-6">
            <div className="text-center">
              <p className="text-2xl font-semibold mb-2">
                {prayerTimes.religion === 'islam' ? 'Islamische Gebetszeiten' : 'Christliche Gebetszeiten'}
              </p>
              <p className="text-3xl font-bold">{prayerTimes.date}</p>
              {prayerTimes.location?.city && (
                <p className="text-xl mt-2">{prayerTimes.location.city}</p>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* All Prayer Times */}
      <div className="max-w-5xl mx-auto space-y-4">
        {prayerTimes?.times && prayerTimes.times.map((time) => (
          <Card
            key={time.name}
            className={`shadow-lg border-3 ${getPrayerColor(time.name.toLowerCase())}`}
          >
            <CardContent className="pt-6 pb-6">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-4">
                  <Moon className="w-14 h-14 text-gray-700" />
                  <div className="flex flex-col">
                    <span className="text-3xl md:text-4xl font-bold text-gray-900">{time.name}</span>
                    {time.arabic && (
                      <span className="text-2xl text-gray-600">{time.arabic}</span>
                    )}
                    {time.description && (
                      <span className="text-xl text-gray-500">{time.description}</span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Clock className="w-10 h-10 text-gray-600" />
                  <span className="text-3xl md:text-4xl font-bold text-gray-900">
                    {time.time}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
